/**
 * Calendar page. Shows race weekends with completed/live/upcoming statuses.
 */
import React from "react";
import { motion } from "framer-motion";
import { Calendar as CalendarIcon, CalendarX, MapPin, Clock } from "lucide-react";
import { api, useFetch } from "../lib/api";
import { EmptyState, LiveStatusPill, CountdownWidget, Reveal } from "../components/atoms";

function raceStartTime(race) {
    const value = race?.race_start || race?.race_start_time_utc || race?.start_time;
    const timestamp = Date.parse(value || "");
    return Number.isFinite(timestamp) ? timestamp : Number.MAX_SAFE_INTEGER;
}

function raceRound(race) {
    const round = Number(race?.round);
    return Number.isFinite(round) ? round : Number.MAX_SAFE_INTEGER;
}

function calendarSortGroup(race) {
    if (race?.status === "live") return 0;
    if (race?.status === "completed") return 2;
    return 1;
}

export function orderCalendarRaces(races = []) {
    return [...races].sort((left, right) => {
        const groupDelta = calendarSortGroup(left) - calendarSortGroup(right);
        if (groupDelta !== 0) return groupDelta;

        const timeDelta = raceStartTime(left) - raceStartTime(right);
        if (timeDelta !== 0) return timeDelta;

        const roundDelta = raceRound(left) - raceRound(right);
        if (roundDelta !== 0) return roundDelta;

        return String(left?.name || "").localeCompare(String(right?.name || ""));
    });
}

function normalizeRaceText(value) {
    return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim();
}

function raceNameKey(race) {
    return normalizeRaceText(race?.slug || race?.name || race?.race_name || race?.official_name);
}

function sameRace(left, right) {
    if (!left || !right) return false;

    const leftId = left.id ? String(left.id) : "";
    const rightId = right.id ? String(right.id) : "";
    if (leftId && rightId && leftId === rightId) return true;

    const leftSeason = left.season != null ? String(left.season) : "";
    const rightSeason = right.season != null ? String(right.season) : "";
    const leftRound = left.round != null ? String(left.round) : "";
    const rightRound = right.round != null ? String(right.round) : "";
    if (leftSeason && rightSeason && leftRound && rightRound && leftSeason === rightSeason && leftRound === rightRound) {
        return true;
    }

    const leftName = raceNameKey(left);
    const rightName = raceNameKey(right);
    return Boolean(leftName && rightName && leftName === rightName);
}

export function getCalendarDisplayRaces(races = [], featuredRace = null) {
    return orderCalendarRaces(races).filter((race) => !sameRace(race, featuredRace));
}

export function getRaceLaps(race) {
    const value = race?.laps ?? race?.total_laps ?? race?.race_laps;
    const numeric = Number(value);
    if (Number.isFinite(numeric) && numeric > 0) return String(Math.round(numeric));
    return "TBD";
}

export function getRaceLength(race) {
    const value = race?.circuit_length_km ?? race?.length_km ?? race?.circuit_length;
    const numeric = Number(value);
    if (Number.isFinite(numeric) && numeric > 0) return `${numeric.toFixed(3)} km`;
    return "TBD";
}

/**
 * Which block the calendar body shows. The API failing is a different state from the API answering
 * with no races; neither is rendered as a blank page.
 */
export function getCalendarViewState({ loading, error, races }) {
    if (loading) return "loading";
    if (error) return "error";
    if (!Array.isArray(races) || races.length === 0) return "empty";
    return "list";
}

export default function Calendar() {
    const { data, loading, error } = useFetch(() => api.scheduleCurrent(), []);
    const races = data?.races || [];
    const viewState = getCalendarViewState({ loading, error, races });
    const orderedRaces = orderCalendarRaces(races);
    const liveRace = orderedRaces.find((r) => r.status === "live");
    const nextRace = orderedRaces.find((r) => r.status === "upcoming");
    const featuredRace = liveRace || nextRace;
    const displayedRaces = getCalendarDisplayRaces(races, featuredRace);
    const season = data?.season || races[0]?.season || new Date().getFullYear();

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="calendar-page">
            <div className="flex flex-col gap-2 mb-10">
                <span className="label-eyebrow text-f1-red">{season} SEASON</span>
                <h1 className="font-display text-5xl sm:text-6xl tracking-tight leading-none">
                    Race Calendar.
                </h1>
                <p className="text-zinc-400 max-w-2xl mt-2">
                    All session times shown in your local timezone. Live races and upcoming weekends are highlighted.
                </p>
            </div>

            {/* HIGHLIGHT next/live */}
            {featuredRace && (
                <Reveal>
                    <div className="mb-8 relative overflow-hidden rounded-2xl border border-f1-red/30 p-6 sm:p-8">
                        <div className="absolute inset-0 bg-gradient-to-br from-f1-red/15 to-transparent" />
                        <div className="absolute inset-0 diagonal-stripes opacity-30" />
                        <div className="relative flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
                            <div>
                                {liveRace ? (
                                    <LiveStatusPill status="live" label="RACE IN PROGRESS" />
                                ) : (
                                    <LiveStatusPill status="upcoming" label="NEXT WEEKEND" />
                                )}
                                <h2 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight">
                                    {featuredRace.name}
                                </h2>
                                <p className="mt-2 text-zinc-300 text-sm">
                                    {featuredRace.circuit} · {featuredRace.location}
                                </p>
                            </div>
                            {nextRace && !liveRace && (
                                <CountdownWidget target={nextRace.race_start} />
                            )}
                        </div>
                    </div>
                </Reveal>
            )}

            {/* Race list */}
            {viewState === "loading" ? (
                <div className="space-y-3">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="h-24 rounded-xl shimmer" />
                    ))}
                </div>
            ) : viewState === "error" ? (
                <EmptyState
                    title="Calendar unavailable"
                    message="The schedule API did not respond. Refresh the page — if this keeps happening the backend may be down."
                    icon={CalendarX}
                />
            ) : viewState === "empty" ? (
                <EmptyState title="No races published" message="The schedule has no race weekends for this season yet." icon={CalendarIcon} />
            ) : (
                <div className="space-y-3">
                    {displayedRaces.map((r, i) => (
                        <Reveal key={r.id} delay={Math.min(i * 0.03, 0.2)}>
                            <RaceRow race={r} />
                        </Reveal>
                    ))}
                </div>
            )}
        </div>
    );
}

function RaceRow({ race }) {
    const start = new Date(race.race_start);
    const statusColor = {
        live: "border-f1-red/40 bg-f1-red/5",
        upcoming: "border-emerald-400/30 bg-emerald-400/5",
        completed: "border-white/5 bg-graphite-800/40 opacity-80",
    }[race.status] || "border-white/5 bg-graphite-800/40";

    return (
        <div
            className={`relative rounded-xl border ${statusColor} p-5 sm:p-6 flex flex-col lg:flex-row gap-5 lg:items-center`}
            data-testid={`race-row-${race.id}`}
        >
            <div className="flex items-center gap-4 lg:w-1/3">
                <div className="font-display text-4xl text-zinc-700 tabular-nums w-12">
                    R{race.round}
                </div>
                <div>
                    <h3 className="font-display text-2xl tracking-tight">{race.name}</h3>
                    <div className="flex items-center gap-2 text-xs text-zinc-500 mt-1">
                        <MapPin className="h-3 w-3" />
                        <span>{race.location}, {race.country}</span>
                    </div>
                </div>
            </div>

            <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Meta label="Race Start" value={start.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })} />
                <Meta label="Laps" value={getRaceLaps(race)} />
                <Meta label="Length" value={getRaceLength(race)} />
                <Meta label="Sessions" value={race.sessions?.length || 0} />
            </div>

            <div className="lg:w-32 flex lg:justify-end">
                <StatusTag status={race.status} />
            </div>
        </div>
    );
}

function Meta({ label, value }) {
    return (
        <div>
            <div className="font-mono text-[9px] tracking-widest text-zinc-500 uppercase">{label}</div>
            <div className="text-sm text-zinc-200 mt-0.5">{value}</div>
        </div>
    );
}

function StatusTag({ status }) {
    if (status === "live") return <LiveStatusPill status="live" />;
    if (status === "upcoming") return <LiveStatusPill status="upcoming" />;
    return (
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 text-zinc-500 border border-white/10">
            <span className="h-1.5 w-1.5 rounded-full bg-zinc-600" />
            <span className="font-mono text-[10px] tracking-widest">COMPLETED</span>
        </span>
    );
}
